import { addDays, isoWeekday } from './calendar.js';
import type { IsoDate } from './calendar.js';
import { mondayOf } from './iso-week.js';
import type { IsoWeek } from './iso-week.js';
import type { EmploymentPeriod } from './capacity.js';

/**
 * Die Zugehoerigkeit eines Mitarbeiters zu einer Fachgruppe ueber die Zeit
 * (AC-104) — die EINE Stelle, an der sie bestimmt wird.
 *
 * Die Zuordnung ist eine Historie aus „ab Datum X gehoert die Person zu
 * Fachgruppe Y", dieselbe Form wie die Soll-Wochenstunden (AC-103): ein Eintrag
 * gilt bis zum Tag vor dem naechsten. Eine `bis`-Spalte gibt es bewusst nicht;
 * zwei Fachgruppen an einem Tag sind damit nicht darstellbar statt geprueft.
 *
 * Zwei Regeln machen daraus eine eindeutige Zuordnung JE WOCHE, und beide
 * sichert die Datenbank (Trigger in `*_ac104_fachgruppen_zuordnung`):
 *
 *   1. Die erste Zuordnung gilt ab dem Eintritt (spaetestens am Eintrittstag).
 *      Ab dem Eintritt bleibt kein Tag ohne Fachgruppe.
 *   2. Jeder WECHSEL beginnt an einem Montag. Die Auslastung mit
 *      Fachgruppensummen ordnet eine Person „wochengenau der jeweils gueltigen
 *      Fachgruppe" zu (AC-407), und eine Wochenbelegung (AC-401) laesst sich
 *      nicht auf zwei Fachgruppen aufteilen. Ein Wechsel mitten in der Woche
 *      haette die Person fuer diese Woche in zwei Wochenraster gestellt (AC-402)
 *      und zwei Fachgruppenleitungen ueber dieselbe Anfrage entscheiden lassen
 *      (AC-404).
 *
 * Wer kuenftig wissen muss, zu welcher Fachgruppe jemand gehoert — das
 * Wochenraster der eigenen Fachgruppe (AC-402), die Auslastung mit
 * Fachgruppensummen (AC-407), die anonyme Ressourcenanfrage (AC-404) —, ruft
 * diese Funktionen auf und rechnet nicht daneben.
 *
 * ZUORDNUNG und BESCHAEFTIGUNG sind zwei Fragen. Wer erst im November eintritt,
 * gehoert schon heute zu der Fachgruppe, in der er anfaengt — das Wochenraster
 * zeigt ihn dort mit gesperrten Wochen vor dem Eintritt (AC-402). Ob eine Woche
 * Kapazitaet hat, sagt weiterhin `capacity.ts`.
 *
 * Seit der AC-103-Ergaenzung (JF 07.10.2026) ist die GESELLSCHAFT dieselbe Art
 * Historie (`company_assignments`, dieselben Regeln, SQLSTATE AC103) — mit
 * einer Einschraenkung fuer beide: Pflicht sind sie nur fuer planbare Personen
 * (`isPlannable` in `capacity.ts`). Externe und Stammsaetze aus PACS, die noch
 * zu vervollstaendigen sind, haben keine Zuordnung; die Funktionen hier
 * liefern fuer sie `null`.
 *
 * Transportfrei (ADR-04) und OHNE Zod: die Oberflaeche rechnet mit denselben
 * Funktionen und laedt sie ueber `@asas/domain/membership` (CLAUDE.md Regel 22,
 * letzter Punkt). Die Schemata dazu stehen in `employees.ts` und
 * `organisation.ts`.
 */

/** Ein Eintrag einer Zuordnungs-Historie: gilt ab `effectiveFrom` bis zum Tag vor dem naechsten. */
export interface AssignmentEntry {
  readonly effectiveFrom: IsoDate;
}

/** „Ab `effectiveFrom` gehoert die Person zu `fachgruppeId`." */
export interface FachgruppeAssignmentEntry extends AssignmentEntry {
  readonly fachgruppeId: string;
}

/** „Ab `effectiveFrom` gehoert die Person zu `companyId`" (AC-103, Ergaenzung). */
export interface CompanyAssignmentEntry extends AssignmentEntry {
  readonly companyId: string;
}

export interface MembershipEmployee extends EmploymentPeriod {
  readonly fachgruppen: readonly FachgruppeAssignmentEntry[];
}

/** Beginnt an diesem Tag eine ISO-Kalenderwoche? Nur dort darf ein Wechsel beginnen. */
export function isWeekStart(date: IsoDate): boolean {
  return isoWeekday(date) === 1;
}

/** Aufsteigend nach Wirksamkeitsdatum, ohne die Eingabe zu veraendern. */
function sortiert<T extends AssignmentEntry>(entries: readonly T[]): T[] {
  return [...entries].sort((a, b) =>
    a.effectiveFrom < b.effectiveFrom ? -1 : a.effectiveFrom > b.effectiveFrom ? 1 : 0,
  );
}

/**
 * Der Eintrag, der an diesem Tag gilt: der mit dem spaetesten
 * Wirksamkeitsdatum, das nicht nach dem Tag liegt. `null`, wenn der Tag vor dem
 * ersten Eintrag liegt. Die Reihenfolge der Eintraege spielt keine Rolle.
 */
export function assignmentOn<T extends AssignmentEntry>(
  entries: readonly T[],
  date: IsoDate,
): T | null {
  let gilt: T | null = null;

  for (const entry of entries) {
    if (
      entry.effectiveFrom <= date &&
      (gilt === null || entry.effectiveFrom > gilt.effectiveFrom)
    ) {
      gilt = entry;
    }
  }

  return gilt;
}

/** Die Fachgruppe, die laut Historie an diesem Tag gilt — `null` vor dem ersten Eintrag. */
export function fachgruppeOn(
  entries: readonly FachgruppeAssignmentEntry[],
  date: IsoDate,
): string | null {
  return assignmentOn(entries, date)?.fachgruppeId ?? null;
}

/**
 * Der Tag, an dem sich die Zuordnung bemisst: der Tag selbst, eingegrenzt auf
 * die Beschaeftigung. Vor dem Eintritt zaehlt der Eintrittstag (die Fachgruppe,
 * in der die Person anfaengt), nach dem Austritt der letzte Arbeitstag (die
 * Fachgruppe, in der sie zuletzt war).
 */
export function assignmentReferenceDay(period: EmploymentPeriod, date: IsoDate): IsoDate {
  if (period.startsOn !== null && date < period.startsOn) return period.startsOn;
  if (period.endsOn !== null && date > period.endsOn) return period.endsOn;

  return date;
}

/**
 * Die Fachgruppe, der die Person an diesem Tag zugeordnet ist — auch vor dem
 * Eintritt (die beim Eintritt) und nach dem Austritt (die zuletzt geltende).
 * `null` nur, wenn die Historie den Eintritt nicht deckt, und das laesst die
 * Datenbank nicht zu.
 */
export function assignedFachgruppeOn(employee: MembershipEmployee, date: IsoDate): string | null {
  return fachgruppeOn(employee.fachgruppen, assignmentReferenceDay(employee, date));
}

/** Die aktuelle Fachgruppe (AK-1): die heute zugeordnete. */
export function currentFachgruppe(employee: MembershipEmployee, today: IsoDate): string | null {
  return assignedFachgruppeOn(employee, today);
}

/** Die Gesellschaft, die laut Historie an diesem Tag gilt — `null` vor dem ersten Eintrag. */
export function companyOn(
  entries: readonly CompanyAssignmentEntry[],
  date: IsoDate,
): string | null {
  return assignmentOn(entries, date)?.companyId ?? null;
}

/**
 * Die aktuelle Gesellschaft: die heute zugeordnete, eingegrenzt auf die
 * Beschaeftigung wie bei der Fachgruppe. `null` ohne Zuordnung (extern, zu
 * vervollstaendigen).
 */
export function currentCompany(
  employee: EmploymentPeriod & { readonly companies: readonly CompanyAssignmentEntry[] },
  today: IsoDate,
): string | null {
  return companyOn(employee.companies, assignmentReferenceDay(employee, today));
}

/**
 * Die Fachgruppe einer ganzen Kalenderwoche (AC-407: „wochengenau"). Eindeutig,
 * weil ein Wechsel nur an einem Montag beginnt: an jedem beschaeftigten Tag der
 * Woche gilt dieselbe Fachgruppe wie an ihrem Montag — eingegrenzt auf die
 * Beschaeftigung, damit die Eintrittswoche die Fachgruppe ab dem Eintritt traegt.
 */
export function fachgruppeOfWeek(employee: MembershipEmployee, week: IsoWeek): string | null {
  return assignedFachgruppeOn(employee, mondayOf(week));
}

/**
 * Wie die Person an einem Stichtag zur Fachgruppe steht.
 *
 *   beschaeftigt      zugeordnet und an dem Tag beschaeftigt
 *   eintritt_geplant  zugeordnet, der Eintritt kommt noch
 */
export const MEMBER_STATUSES = ['beschaeftigt', 'eintritt_geplant'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

/**
 * Gehoert die Person am Stichtag zur Fachgruppe, und wie? `null`, wenn sie einer
 * anderen zugeordnet ist — oder wenn sie vor dem Stichtag ausgetreten ist: wer
 * gegangen ist, gehoert keiner Fachgruppe mehr an, auch nicht der letzten.
 *
 * Das ist die Antwort auf AK-1 („alle aktuell zugeordneten Mitarbeiter"):
 * beschaeftigte Mitglieder und die, deren Eintritt in dieser Fachgruppe geplant
 * ist. „Geplant" gibt es nur von heute aus: an einem Stichtag in der
 * VERGANGENHEIT gehoert, wer erst spaeter eintrat, nicht zur damaligen
 * Besetzung — ob sein Eintritt damals schon feststand, weiss niemand.
 */
export function memberStatusOn(
  employee: MembershipEmployee,
  fachgruppeId: string,
  date: IsoDate,
  today: IsoDate,
): MemberStatus | null {
  if (employee.endsOn !== null && employee.endsOn < date) return null;
  if (assignedFachgruppeOn(employee, date) !== fachgruppeId) return null;

  if (employee.startsOn !== null && employee.startsOn > date) {
    return date < today ? null : 'eintritt_geplant';
  }

  return 'beschaeftigt';
}

/** Die zusammenhaengende Zugehoerigkeit, in der ein Tag liegt. */
export interface Membership {
  readonly fachgruppeId: string;
  /**
   * Seit wann die Person ohne Unterbrechung dieser Fachgruppe zugeordnet ist.
   * Beginnt die Zugehoerigkeit mit dem ersten Eintrag, ist das der Eintritt —
   * `null` bei Altbestand ohne Eintrittsdatum („seit jeher").
   */
  readonly since: IsoDate | null;
  /**
   * Der letzte Tag in dieser Fachgruppe, wenn ein Wechsel in eine ANDERE
   * geplant ist; sonst `null`. Der Austritt ist bewusst NICHT eingerechnet: er
   * ist ein Personaldatum (`employee:manage`, AC-103), die Zugehoerigkeit ein
   * Organisationsdatum. Ein Wechsel, der erst nach dem Austritt laege, wirkte
   * nie und erscheint deshalb auch nicht.
   */
  readonly until: IsoDate | null;
}

/**
 * Die Zugehoerigkeit an diesem Tag (eingegrenzt auf die Beschaeftigung wie
 * `assignedFachgruppeOn`), mit Beginn und geplantem Ende — aufeinanderfolgende
 * Eintraege derselben Fachgruppe zaehlen als EINE. `null`, wenn die Historie den
 * Tag nicht deckt.
 */
export function membershipOn(employee: MembershipEmployee, date: IsoDate): Membership | null {
  const eintraege = sortiert(employee.fachgruppen);
  const gilt = assignmentOn(eintraege, assignmentReferenceDay(employee, date));

  if (gilt === null) return null;

  const index = eintraege.indexOf(gilt);
  let anfang = index;

  while (anfang > 0 && eintraege[anfang - 1]?.fachgruppeId === gilt.fachgruppeId) {
    anfang -= 1;
  }

  const lauf = eintraege[anfang] ?? gilt;
  // Der erste Lauf beginnt mit dem Eintritt, auch wenn der erste Eintrag
  // frueher datiert (ein nach hinten verschobener Eintritt). Spaetere Laeufe
  // beginnen mit ihrem Eintrag — fruehestens aber mit dem Eintritt.
  const since =
    anfang === 0
      ? employee.startsOn
      : employee.startsOn !== null && lauf.effectiveFrom < employee.startsOn
        ? employee.startsOn
        : lauf.effectiveFrom;

  const wechsel = eintraege
    .slice(index + 1)
    .find((entry) => entry.fachgruppeId !== gilt.fachgruppeId);
  const until =
    wechsel === undefined || (employee.endsOn !== null && wechsel.effectiveFrom > employee.endsOn)
      ? null
      : addDays(wechsel.effectiveFrom, -1);

  return { fachgruppeId: gilt.fachgruppeId, since, until };
}
